/**
 * Customer-portal empty state that replaces "Contact us to add students..."
 * with an "Add Student" CTA and a live list of the customer's pending /
 * approved / rejected `StudentAddSubmission` records (OWZ-9 Round 3).
 *
 * Used by CustomerDashboard.jsx where `students.length === 0`. Drives the
 * AddStudentForm modal and renders the customer's submission history below
 * so they can see status updates without refreshing.
 */
import { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, Clock, CheckCircle, XCircle, Trash2 } from 'lucide-react';

import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

import AddStudentForm from './AddStudentForm';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('memberToken') || localStorage.getItem('customerToken')}`,
  'Content-Type': 'application/json',
});

const STATUS_META = {
  pending: {
    Icon: Clock,
    label: 'Pending review',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
  approved: {
    Icon: CheckCircle,
    label: 'Approved',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    iconClass: 'text-green-600 dark:text-green-400',
  },
  rejected: {
    Icon: XCircle,
    label: 'Not added',
    badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    iconClass: 'text-gray-500 dark:text-gray-400',
  },
};

const AddStudentEmptyState = ({ onStudentApproved }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/add-submissions`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setSubmissions(Array.isArray(data) ? data : []);
        // If an admin just approved one, the parent dashboard needs to
        // refetch its `students` list so the dashboard exits this empty
        // state. Fire onStudentApproved when we observe any approved row.
        if (Array.isArray(data) && data.some((s) => s.status === 'approved')) {
          onStudentApproved?.();
        }
      }
    } catch {
      // Soft-fail: empty state still works as a CTA-only screen.
    } finally {
      setLoading(false);
    }
  }, [onStudentApproved]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleWithdraw = async (submissionId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/add-submissions/${submissionId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      if (response.ok) {
        setSubmissions((current) => current.filter((s) => s.id !== submissionId));
      }
    } catch {
      // Caller stays on the screen; refetch will recover on next mount.
    }
  };

  return (
    <>
      <Card>
        <CardContent className="py-8 text-center">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <Users className="h-12 w-12 mx-auto" />
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            No students found on your account.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 mb-4">
            Add a student to your membership and an instructor will set them up.
          </p>
          <Button onClick={() => setFormOpen(true)} className="inline-flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Add Student
          </Button>
        </CardContent>
      </Card>

      {!loading && submissions.length > 0 && (
        <Card className="mt-4">
          <CardContent className="py-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Your submissions
            </p>
            <ul className="space-y-2">
              {submissions.map((submission) => {
                const meta = STATUS_META[submission.status] || STATUS_META.pending;
                const Icon = meta.Icon;
                return (
                  <li
                    key={submission.id}
                    className="flex items-start justify-between gap-3 p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <Icon className={`h-4 w-4 mt-1 ${meta.iconClass}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {submission.student_name}
                        </p>
                        <span
                          className={`inline-block text-xs px-2 py-0.5 rounded mt-1 ${meta.badge}`}
                        >
                          {meta.label}
                        </span>
                        {submission.status === 'rejected' && submission.reviewer_notes && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                            Instructor note: {submission.reviewer_notes}
                          </p>
                        )}
                      </div>
                    </div>
                    {submission.status === 'pending' && (
                      <button
                        type="button"
                        onClick={() => handleWithdraw(submission.id)}
                        aria-label={`Withdraw submission for ${submission.student_name}`}
                        className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      <AddStudentForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmitted={fetchSubmissions}
      />
    </>
  );
};

export default AddStudentEmptyState;
